import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  CloudinaryImage,
  IllusDelivery_1,
  IllusOutpost_1,
  Image,
  theme,
} from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  LOCATION_CARD_IMG_BG_COLOR,
  LOCATION_CARD_IMG_CLOUDINARY_CONFIG,
  LOCATION_RESULTS_CARD_IMG_SIZE,
} from '../../../../LocationResultsV2.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultCardImg = (props: LocationResultCardImgProps) => {
  const { locationName, locationSearchType, imageUrl } = props;

  const { t } = useLocalizationContext();

  // NOTE: For outpost and delivery locations, we use static illustrations,
  //       and for pickup locations, we use a remote image.

  // ─── Image alt text ──────────────────────────────────────────────────

  const shouldUseDynamicImageAlt =
    locationSearchType === 'pickup' && Boolean(locationName);
  const imageAlt = shouldUseDynamicImageAlt
    ? locationName
    : t('location.result.img-alt', { locationSearchType });

  // ─── Render conditions ───────────────────────────────────────────────

  const shouldRenderPickupImg =
    locationSearchType === 'pickup' && imageUrl !== undefined;
  const shouldRenderDeliveryIllustration = locationSearchType === 'delivery';
  const shouldRenderOutpostIllustration = locationSearchType === 'outpost';

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderPickupImg)
    return (
      <LocationResultCardImgContainer>
        <CloudinaryImage
          baseUrl={imageUrl}
          config={LOCATION_CARD_IMG_CLOUDINARY_CONFIG}
          resizeMode="cover"
          style={styles.img}
          accessibilityLabel={imageAlt}
        />
      </LocationResultCardImgContainer>
    );

  if (shouldRenderDeliveryIllustration)
    return (
      <LocationResultCardImgContainer>
        <Image
          source={IllusDelivery_1}
          style={styles.img}
          resizeMode="cover"
          accessibilityLabel={imageAlt}
          accessibilityIgnoresInvertColors
        />
      </LocationResultCardImgContainer>
    );

  if (shouldRenderOutpostIllustration)
    return (
      <LocationResultCardImgContainer>
        <Image
          source={IllusOutpost_1}
          style={styles.img}
          resizeMode="cover"
          accessibilityLabel={imageAlt}
          accessibilityIgnoresInvertColors
        />
      </LocationResultCardImgContainer>
    );

  return null;
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const LocationResultCardImgContainer = (
  props: LocationResultCardImgContainerProps,
) => {
  const { children, withBgColor = false } = props;

  const imgContainerStyles = [
    styles.imgContainer,
    withBgColor ? styles.imgContainerWithBg : undefined,
  ];

  return <View style={imgContainerStyles}>{children}</View>;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultCardImgProps = {
  readonly locationName: string;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly imageUrl?: string;
};

type LocationResultCardImgContainerProps = {
  readonly children: ViewProps['children'];
  readonly withBgColor?: boolean;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  imgContainer: {
    borderRadius: theme.spacing.small,
    marginRight: 12,
  },
  imgContainerWithBg: {
    backgroundColor: LOCATION_CARD_IMG_BG_COLOR,
  },
  img: {
    width: LOCATION_RESULTS_CARD_IMG_SIZE,
    height: LOCATION_RESULTS_CARD_IMG_SIZE,
    borderRadius: theme.spacing.small,
  },
});
